class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    // Le tiroir nomme ses champs `Drawer-quantity-N`, pas `Quantity-N` : chercher le seul
    // nom de la page renvoyait null et levait une TypeError dès qu'une quantité hors
    // bornes était saisie DANS le tiroir (saisir 0, par exemple, `data-min` valant 1).
    const input = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          // L'en-tête porte le nombre d'articles : sans lui, un ajout depuis une suggestion du tiroir laissait
          // « 1 article » au-dessus de deux lignes. Le palier et les suggestions vivent dans <cart-drawer-items>.
          const selectors = ['.sc-drawer__header', 'cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      // L'id d'INSTANCE (`template--<id>__cart-securidou`, porté par data-id) est le seul
      // que l'API Section Rendering résout avec les réglages réels du template JSON.
      // Le nom de fichier (`main-cart-securidou`) répond bien 200, mais rend la section
      // AUTONOME, donc avec les réglages par défaut du schema : cross_sell_collection n'a
      // pas de `default`, le bloc cross-sell disparaît, et data-id est réécrit au passage
      // ce qui contamine tous les rendus suivants. Le nom de fichier ne reste qu'en
      // dernier recours si l'ancre est absente.
      const sectionId = this.getSectionId();
      return fetch(`${routes.cart_url}?section_id=${encodeURIComponent(sectionId)}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionId() {
    const mainCartItems = document.getElementById('main-cart-items');
    return (mainCartItems && mainCartItems.dataset.id) || this.dataset.sectionType || 'main-cart-items';
  }

  getSectionsToRender() {
    const cartSectionId = this.getSectionId();
    const sections = [
      {
        id: 'main-cart-items',
        section: cartSectionId,
        selector: '#main-cart-items .js-contents',
      },
    ];

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      });
    }

    if (document.getElementById('cart-live-region-text') &&
        !document.getElementById('main-cart-items').contains(document.getElementById('cart-live-region-text'))) {
      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '#main-cart-footer .js-contents',
      });
    }

    // Zones de la page panier qui vivent HORS des deux `.js-contents` (compteur du titre,
    // total de la barre sticky mobile, palier PANIER10, suggestions) et qui restaient donc
    // figées après un changement de quantité. Elles proviennent de la MÊME section, déjà
    // demandée plus haut : aucun aller-retour réseau supplémentaire, juste une extraction de plus.
    ['sc-cart-count', 'sc-cart-sticky-total', 'sc-cart-palier', 'sc-cart-cross-sell'].forEach((anchorId) => {
      if (document.getElementById(anchorId)) {
        sections.push({ id: anchorId, section: cartSectionId, selector: `#${anchorId}` });
      }
    });

    // Sur /cart le tiroir est rendu lui aussi : sans ça, modifier une quantité sur la page
    // puis ouvrir le tiroir depuis l'en-tête affichait des quantités et un total périmés
    // (l'abonné du tiroir ignore `source: 'cart-items'`).
    const cartDrawer = document.getElementById('CartDrawer');
    if (cartDrawer && cartDrawer.querySelector('.drawer__inner')) {
      sections.push({
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      });
    }

    return sections;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    // Dédupliqué : plusieurs zones proviennent de la MÊME section (les deux `.js-contents`
    // plus les trois ancres), ce qui faisait 8 entrées pour 4 sections réelles. Shopify
    // déduplique de son côté, mais l'API plafonne à 5 sections par requête : sans ce Set,
    // une sixième zone ajoutée un jour ferait sauter la limite sans que rien ne l'annonce.
    const body = JSON.stringify({
      line,
      quantity,
      sections: [...new Set(this.getSectionsToRender().map((section) => section.section))],
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          // Ces deux résolutions sont scopées à CET élément AVANT de retomber sur le
          // document, et les deux pour la même raison : sur /cart la page et le tiroir
          // coexistent, chacun avec ses propres champs de quantité et ses propres lignes.
          //   - `items` document-wide valait 2 × le nombre de lignes, donc la comparaison
          //     plus bas n'était jamais vraie et le message « quantité ajustée au stock »
          //     n'apparaissait jamais sur la page panier ;
          //   - `quantityElement` prenait TOUJOURS l'input de la page (`Quantity-N` gagne
          //     le `||`), même quand la modification venait du tiroir. Il portait alors
          //     l'ancienne quantité, ce qui déclenchait un faux message d'ajustement.
          // Le repli sur le document reste pour les pages où seul le tiroir existe.
          const quantityElement =
            this.querySelector(`#Quantity-${line}`) ||
            this.querySelector(`#Drawer-quantity-${line}`) ||
            document.getElementById(`Quantity-${line}`) ||
            document.getElementById(`Drawer-quantity-${line}`);
          const items = this.querySelectorAll('.cart-item, .sc-cart__item, .sc-drawer__item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          const estVide = parsedState.item_count === 0;
          this.classList.toggle('is-empty', estVide);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', estVide);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', estVide);

          // `this` ne couvre qu'un des deux affichages. Sur /cart ils coexistent, et depuis
          // que chacun rafraîchit l'autre, vider le panier depuis le tiroir mettait bien la
          // liste de la page à jour mais laissait `<cart-items>` sans `is-empty` : la page
          // montrait une liste vide au lieu de son état « Votre panier est vide ».
          // `<cart-drawer-items>`, lui, est recréé avec la bonne classe puisqu'il vit à
          // l'intérieur de `.drawer__inner` dont on remplace le contenu.
          const cartItemsPage = document.querySelector('cart-items');
          if (cartItemsPage && cartItemsPage !== this) {
            cartItemsPage.classList.toggle('is-empty', estVide);
          }

          // Chaque zone est traitée indépendamment : une ancre absente d'un côté ou de
          // l'autre saute son tour au lieu de lever une TypeError qui interromprait la
          // boucle et laisserait la page à moitié rafraîchie (totaux à jour, lignes non).
          this.getSectionsToRender().forEach((section) => {
            const target = document.getElementById(section.id);
            if (!target) return;
            const html = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            if (html === null) return;
            (target.querySelector(section.selector) || target).innerHTML = html;
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item, .sc-drawer__item, .sc-cart__item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name, .sc-drawer__item-name, .sc-cart__item-name'));
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  // Renvoie null (et non '') quand la source est absente : un '' écraserait la zone avec
  // du vide, alors que l'appelant doit simplement laisser la zone en place.
  getSectionInnerHTML(html, selector) {
    if (typeof html !== 'string') return null;
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

if (!customElements.get('cart-cross-sell-add')) {
  customElements.define(
    'cart-cross-sell-add',
    class CartCrossSellAdd extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('button');
        if (this.button) {
          this.button.addEventListener('click', this.addToCart.bind(this));
        }
      }

      async addToCart(e) {
        e.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        this.button.setAttribute('aria-disabled', 'true');
        this.button.classList.add('sc-cross-sell__loading');

        // Pas de `sections` dans la charge utile : la réponse n'était lue par aucun abonné
        // (ils lisent item_count / items / variant_id), et le rafraîchissement passe de
        // toute façon par onCartUpdate. Les demander renvoyait ~33 Ko de HTML jeté.
        const body = JSON.stringify({
          id: parseInt(this.dataset.variantId),
          quantity: 1,
        });

        // Le bouton est remplacé avec sa zone par le rendu qui suit : on note où il vivait avant.
        const dansTiroir = Boolean(this.closest('cart-drawer-items'));
        try {
          const response = await fetch(routes.cart_add_url, { ...fetchConfig(), body });
          const data = await response.json();

          if (data.status) {
            console.error(data.description);
            return;
          }

          // `publish` suffit : l'abonné de CartItems (et celui de CartDrawerItems) appelle
          // onCartUpdate car la source n'est pas 'cart-items'. L'appel explicite qui suivait
          // déclenchait un SECOND rendu de la même section pour rien. Il renvoie la promesse
          // de tous les rendus : on l'attend pour replacer le focus sur la ligne ajoutée (la
          // première, Shopify ajoute en tête), sans quoi il tombait sur <body>, hors du tiroir.
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cross-sell',
            productVariantId: this.dataset.variantId,
            cartData: data,
          });
          const tiroir = document.getElementById('CartDrawer');
          if (dansTiroir && tiroir) {
            const ligne = tiroir.querySelector('.sc-drawer__item-name');
            if (ligne) trapFocus(tiroir, ligne);
          } else {
            const ligne = document.querySelector('#main-cart-items .sc-cart__item-name');
            if (ligne) ligne.focus();
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.button.removeAttribute('aria-disabled');
          this.button.classList.remove('sc-cross-sell__loading');
        }
      }
    }
  );
}
